import { forwardRef, type ReactEventHandler } from 'react';
import { m } from '@/paraglide/messages';

interface HouseRulesAccordionProps {
  text: string;
  id?: string;
  className?: string;
  // Both omitted => a plain uncontrolled <details> (apartment page: opens/
  // closes purely via its own <summary>). Both supplied => controlled
  // (checkout page: the consent checkbox's "House Rules" link can force it
  // open and scroll to it, one click, without a second copy of the text).
  open?: boolean;
  onToggle?: ReactEventHandler<HTMLDetailsElement>;
}

// Compact, collapsed-by-default accordion for apartments.house_rules
// (OH-69 §5.6/§5.7, spec §7 criterion 8). The field stays plain `text` —
// the owner's call was against structuring it ("there's already formatted
// text in there") — so this renders it verbatim with preserved line breaks,
// never through a markdown parser. Callers must only render this when the
// text is non-empty (see lib/apartments.ts's resolveHouseRules): an
// apartment with no house rules gets no block here, empty or otherwise.
export const HouseRulesAccordion = forwardRef<HTMLDetailsElement, HouseRulesAccordionProps>(
  function HouseRulesAccordion({ text, id, className, open, onToggle }, ref) {
    return (
      <details
        ref={ref}
        id={id}
        open={open}
        onToggle={onToggle}
        className={`rounded-[var(--radius-control)] border border-[var(--border-hairline)] bg-[var(--surface-sunken)] px-4 py-3 ${className ?? ''}`}
      >
        <summary className="cursor-pointer select-none text-sm font-semibold text-[var(--ollio-ink)]">
          {m.house_rules_heading()}
        </summary>
        <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-[var(--text-body)]">{text}</p>
      </details>
    );
  },
);
